// Saved views of the Patients list (UC-PATIENTS-014 step 6, PAT-61; BR-PATIENTS-035): a name and the list's filters and order,
// kept in this browser for the signed-in user of this organisation — client-side in Sprint 2. A view never holds the search
// box's text or anything about a patient: only the URL parameters the toolbar sets.

/** The URL parameters a view keeps — the toolbar's filters and the order; never `q`, the search text. */
export const VIEW_KEYS = ["status", "branch", "scheme", "missingEmail", "incomplete", "consent", "dependants", "archived", "createdFrom", "createdTo", "visitFrom", "visitTo", "sort"] as const;
export const VIEW_NAME_MAX = 40;

export interface SavedView { name: string; params: string }

const storageKey = (orgId: string, userId: string): string => `vx.patients.views.${orgId}.${userId}`;

/** The view parameters of a URL's query, in a fixed order so two equal sets of filters compare equal. */
export function viewParamsOf(sp: URLSearchParams): string {
  const out = new URLSearchParams();
  for (const k of VIEW_KEYS) { const v = sp.get(k); if (v) out.set(k, v); }
  return out.toString();
}

const isView = (v: unknown): v is SavedView => typeof v === "object" && v !== null && typeof (v as SavedView).name === "string" && typeof (v as SavedView).params === "string";

/** The saved views, by name; none when the browser keeps nothing or the stored value is not a list of views. */
export function loadViews(orgId: string, userId: string): SavedView[] {
  try {
    const raw = localStorage.getItem(storageKey(orgId, userId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isView).map((v) => ({ name: v.name, params: viewParamsOf(new URLSearchParams(v.params)) })) : [];
  } catch { return []; }
}

/** Writes the views; false when the browser keeps nothing (a private window, storage blocked or full). */
export function storeViews(orgId: string, userId: string, views: readonly SavedView[]): boolean {
  try {
    localStorage.setItem(storageKey(orgId, userId), JSON.stringify(views));
    return true;
  } catch { return false; }
}

/** The views with `view` added, or replacing the one of the same name (A3), sorted by name. */
export function withView(views: readonly SavedView[], view: SavedView): SavedView[] {
  const key = view.name.toLocaleLowerCase("en-ZA");
  return [...views.filter((v) => v.name.toLocaleLowerCase("en-ZA") !== key), view].sort((a, b) => a.name.localeCompare(b.name, "en-ZA"));
}
